import {Fragment, useEffect, useState} from 'react';
import {NavLink} from 'react-router-dom';
import styled, {keyframes} from 'styled-components';
import {Button, IconButton} from '@/components/Button';
import {Footer} from '@/components/Footer';
import {MobileNavBar} from '@/components/NavBar/MobileNavBar';
import {Typography} from '@/components/Typography';
import {useAirbridgeTracking, useGA4WithParams} from '@/hooks/tracking';
import {paths, SERVICE_GUIDE} from '@/router';
import {
  ColumnFlexBox,
  Divider,
  Flexbox,
  HorizontalFullContainer,
  PaddingBox,
  ScrollBody,
} from '@/styled-components';
import {palette} from '@/styles';
import AccPlusIcon from './acc-add-28.svg';
import AccMinusIcon from './acc-minus-28.svg';
import PartnersLogo from './brand-logo-rolling.png';
import {ReactComponent as IconCheck} from './icon-check.svg';
import MainImg002 from './main002.png';
import MapBg from './map-bg.png';
import OutLinkIcon from './open_in_new.png';
import OutLinkIconGray from './open_in_new_gray.png';
import {ACCORDION_CONTENTS, AccordionContent} from './constants';

export const MobileIntro = () => {
  const tracking = useGA4WithParams();
  const trackAirbridge = useAirbridgeTracking();

  const onGuideClick = () => {
    trackAirbridge('click_preview_page');
    tracking('guide_click', {
      event_callback: () => window.open(SERVICE_GUIDE, '_blank'),
    });
  };

  useEffect(() => {
    const container = document.querySelector('.mobile-logo-slider');
    const slide = document.querySelector('.mobile-logo-slide');
    if (container && slide) {
      const cloneSlide = slide.cloneNode(true);
      container.appendChild(cloneSlide);
    }
  }, []);

  return (
    <HorizontalFullContainer>
      <MobileNavBar />
      <Body>
        <Header>
          <Headline>
            필요한 모든 상권 정보를 한 번에!
            <h1>마이프차 지도</h1>
          </Headline>
          <PaddingBox top={16}>
            <Typography
              variant="medium14"
              color={palette.gray600}
              lineHeight={1.5}
              align="center"
              as="div"
            >
              상권이 가장 중요한 프랜차이즈 창업 시장에서
              <br />
              검증된 상권 분석 전문 서비스
            </Typography>
          </PaddingBox>
          <PaddingBox top={32}>
            <ButtonsWrapper>
              <NavLink to={paths.pricing_root} style={{width: '100%'}}>
                <MobileButton
                  size="large"
                  variant="fill"
                  color="secondary"
                  as={Button}
                >
                  마이프차 지도 요금제 보기
                </MobileButton>
              </NavLink>
              <MobileTealButton
                size="large"
                icon={OutLinkIcon}
                iconSize={18}
                gap="6px"
                reverse
                as={IconButton}
                onClick={onGuideClick}
              >
                미리보기
              </MobileTealButton>
            </ButtonsWrapper>
          </PaddingBox>
          <LogoSlider className="mobile-logo-slider">
            <div className="mobile-logo-slide">
              <img src={PartnersLogo} alt="파트너사 로고" />
              <img src={PartnersLogo} alt="파트너사 로고" />
            </div>
          </LogoSlider>
        </Header>

        <Section>
          <Typography
            as="div"
            color={palette.teal200}
            variant="medium14"
            fontWeight={600}
            lineHeight={1.5}
          >
            마이프차 지도에서만 제공하는 특별한 기능!
          </Typography>
          <PaddingBox top={8} bottom={12}>
            <MobileSectionTitle as="div">AI 상권 분석 해설</MobileSectionTitle>
          </PaddingBox>
          <Typography
            as="div"
            color={palette.gray600}
            variant="medium14"
            lineHeight={1.5}
          >
            데이터를 기반으로 AI가 상권 데이터를 해설해 드려요.
            <br />
            아래 튜토리얼을 따라가 보세요.
          </Typography>
          <PaddingBox top={24}>
            <TutorialLinkButton
              href="https://demo.arcade.software/qDAOvIik8fQfiAqrVwEv"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={OutLinkIcon} alt="" width={18} height={18} />
              튜토리얼 보기
            </TutorialLinkButton>
          </PaddingBox>
        </Section>

        {ACCORDION_CONTENTS.map((item) => (
          <Fragment key={item.title}>
            <DividerWrapper>
              <Divider />
            </DividerWrapper>
            <MobileAccordionSection item={item} />
          </Fragment>
        ))}

        <GraySection>
          <PaddingBox bottom={24}>
            <MobileSectionTitle as="div">
              이런 분들이라면 마이프차 지도를 경험해 보세요.
            </MobileSectionTitle>
          </PaddingBox>
          <MobileRoundBox>
            <Flexbox gap="16px" alignItems="flex-start">
              <IconCheck
                style={{flexShrink: 0, marginTop: 2, width: 20, height: 20}}
              />
              <p>
                상가 경매 투자를 위한 고도화된{' '}
                <strong>상권 분석 서비스가 필요할 때</strong>
              </p>
            </Flexbox>
            <Flexbox gap="16px" alignItems="flex-start">
              <IconCheck
                style={{flexShrink: 0, marginTop: 2, width: 20, height: 20}}
              />
              <p>
                관심 있는 지역이 정말{' '}
                <strong>투자 가치가 있는 상권인지 궁금할 때</strong>
              </p>
            </Flexbox>
            <Flexbox gap="16px" alignItems="flex-start">
              <IconCheck
                style={{flexShrink: 0, marginTop: 2, width: 20, height: 20}}
              />
              <p>
                다음 매장을 오픈할 <strong>지역을 신중하게 찾고 있을 때</strong>
              </p>
            </Flexbox>
            <Flexbox gap="16px" alignItems="flex-start">
              <IconCheck
                style={{flexShrink: 0, marginTop: 2, width: 20, height: 20}}
              />
              <p>
                서비스마다 제공하는 상권 정보가 다 다른데{' '}
                <strong>정리할 시간이 없을 때</strong>
              </p>
            </Flexbox>
            <Flexbox gap="16px" alignItems="flex-start">
              <IconCheck
                style={{flexShrink: 0, marginTop: 2, width: 20, height: 20}}
              />
              <p>
                관심 업종이 이 지역에서{' '}
                <strong>매출이 잘 나올지 궁금할 때</strong>
              </p>
            </Flexbox>
          </MobileRoundBox>
        </GraySection>

        <FooterSection>
          <MobileSectionTitle as="div" style={{color: '#fff'}}>
            지금 바로, 내 인생을 결정할
            <br />
            가장 중요한 비즈니스의
            <br />
            성공 확률을 높여보세요!
          </MobileSectionTitle>
          <PaddingBox top={32}>
            <MobileButton
              size="regular"
              variant="fill"
              color="white"
              icon={OutLinkIconGray}
              iconSize={18}
              gap="4px"
              reverse
              as={IconButton}
              onClick={onGuideClick}
              style={{width: '100%'}}
            >
              마이프차 지도 미리보기
            </MobileButton>
          </PaddingBox>
          <PaddingBox top={32}>
            <img
              src={MainImg002}
              alt="마이프차 지도"
              style={{width: '100%', height: 'auto', display: 'block'}}
            />
          </PaddingBox>
        </FooterSection>

        <Footer />
      </Body>
    </HorizontalFullContainer>
  );
};

const MobileAccordionSection = ({item}: {item: AccordionContent}) => {
  const [activeIndex, setActiveIndex] = useState<number>(0);

  if (!item) return null;

  const active = item.items[activeIndex >= 0 ? activeIndex : 0];

  return (
    <Section>
      <PaddingBox bottom={8}>
        <MobileSectionTitle as="div">{item.title}</MobileSectionTitle>
      </PaddingBox>
      {!!item.desc && (
        <Typography
          as="div"
          color={palette.gray600}
          variant="medium14"
          lineHeight={1.5}
        >
          {item.desc}
        </Typography>
      )}
      <PaddingBox top={24}>
        <img
          src={active?.image}
          alt={active?.title}
          style={{
            width: '100%',
            height: 'auto',
            display: 'block',
            borderRadius: 8,
          }}
        />
      </PaddingBox>
      <PaddingBox top={24}>
        <AccordionList>
          {item.items.map((i, idx) => {
            const isActive = idx === activeIndex;
            return (
              <AccordionItem
                key={i.title}
                onClick={() => setActiveIndex(isActive ? -1 : idx)}
              >
                <AccordionItemInner>
                  <AccordionTitle>{i.title}</AccordionTitle>
                  {isActive &&
                    (typeof i.desc === 'string' ? (
                      <AccordionDesc
                        dangerouslySetInnerHTML={{__html: i.desc}}
                      />
                    ) : (
                      <AccordionDesc>{i.desc}</AccordionDesc>
                    ))}
                </AccordionItemInner>
                <PaddingBox top={2} bottom={2} style={{flexShrink: 0}}>
                  <img
                    src={isActive ? AccMinusIcon : AccPlusIcon}
                    alt={isActive ? '닫기' : '열기'}
                  />
                </PaddingBox>
              </AccordionItem>
            );
          })}
        </AccordionList>
      </PaddingBox>
    </Section>
  );
};

const Body = styled(ScrollBody)`
  padding: 0;
  display: flex;
  flex-direction: column;
`;

const infiniteLoop = keyframes`
  0% { transform: translateX(-100%); }
  100% { transform: translateX(0); }
`;

const LogoSlider = styled.div`
  display: flex;
  overflow: hidden;
  width: 100vw;
  margin-top: 40px;
  .mobile-logo-slide {
    display: flex;
  }
  img {
    animation: ${infiniteLoop} 24s linear infinite;
    width: 100vw;
  }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 20px 0;
  background-image: url(${MapBg});
  background-size: cover;
  background-position: center top;
`;

const Headline = styled.div`
  font-size: 26px;
  line-height: 1.5;
  font-weight: 600;
  color: ${palette.black};
  & > * {
    font-size: inherit;
    line-height: inherit;
    font-weight: inherit;
    margin: 0;
  }
`;

const ButtonsWrapper = styled(ColumnFlexBox)`
  gap: 10px;
  width: 100%;
`;

const MobileButton = styled.button`
  width: 100%;
  padding: 0 16px;
  font-size: 15px;
  font-weight: 600;
  height: 48px;
  border-radius: 10px;
  box-sizing: border-box;
`;

const MobileTealButton = styled(MobileButton)`
  border-color: ${palette.teal200};
  color: ${palette.teal200};
  background-color: ${palette.white};
`;

const Section = styled.div`
  padding: 40px 20px;
`;

const DividerWrapper = styled.div`
  padding: 0 20px;
`;

const MobileSectionTitle = styled.div`
  font-size: 22px;
  font-weight: 600;
  line-height: 1.5;
  color: ${palette.black};
  letter-spacing: -0.66px;
`;

const TutorialLinkButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 48px;
  border-radius: 10px;
  border: 1.5px solid ${palette.teal200};
  color: ${palette.teal200};
  font-size: 15px;
  font-weight: 600;
  text-decoration: none;
  box-sizing: border-box;
`;

const AccordionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AccordionItem = styled.div`
  cursor: pointer;
  width: 100%;
  padding-bottom: 16px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: flex-start;
  &:not(:last-of-type) {
    border-bottom: solid 1px ${palette.gray800};
  }
`;

const AccordionItemInner = styled.div`
  flex: 1;
  min-width: 0;
`;

const AccordionTitle = styled.div`
  color: ${palette.secondary900};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
  letter-spacing: -0.48px;
`;

const AccordionDesc = styled.div`
  color: ${palette.gray600};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  letter-spacing: -0.39px;
  padding-top: 12px;
`;

const GraySection = styled(Section)`
  background-color: #f3f5f7;
`;

const MobileRoundBox = styled(ColumnFlexBox)`
  padding: 28px 20px;
  gap: 24px;
  border-radius: 20px;
  background-color: #fff;
  p {
    margin: 0;
    font-size: 15px;
    color: ${palette.black};
    line-height: 1.5;
  }
`;

const FooterSection = styled(Section)`
  background-color: ${palette.primary500};
`;
